// Builds `solred-transfer-ui/0.2-draft` snapshots from engine state. Pure functions, no I/O.

export const PROTOCOL = 'solred-transfer-ui/0.2-draft';

/** Piece states held in a Uint8Array by the engines. */
export const P = Object.freeze({ PENDING: 0, RECEIVING: 1, RECEIVED: 2, VERIFIED: 3, RETRYING: 4 });
const NAMES = ['pending', 'receiving', 'received', 'verified', 'retrying'];

/** Above this many pieces snapshots carry aggregated `pieceGroups` instead of one entry per piece. */
export const CHUNK_LIST_LIMIT = 4000;
export const GROUP_COUNT = 400;

const NONE = Object.freeze({ start: false, pause: false, resume: false, cancel: false, retry: false, save: false, toggleSharing: false, updateSharingLimits: false });

/**
 * Buttons are derived here, from the engine's real state, never from the view.
 * @param {string} status
 * @param {{ direction?: 'download'|'upload', sharingEligible?: boolean }} o
 */
export function capabilitiesFor(status, o = {}) {
  const c = { ...NONE };
  const up = o.direction === 'upload';
  switch (status) {
    case 'idle': case 'cancelled': c.start = !up; break; // uploads start from the file picker, not a button
    case 'preparing': case 'reconnecting': case 'resuming': case 'verifying': c.cancel = true; break;
    case 'downloading': case 'retrying': case 'uploading': c.pause = true; c.cancel = true; break;
    case 'paused': c.resume = true; c.cancel = true; break;
    case 'offline': case 'storage-blocked': case 'error': case 'expired': c.retry = true; c.cancel = true; break;
    case 'ready': case 'save-error': c.save = !up; break;
    default: break; // pausing, finalizing, committing, saving, saved, gate, unsupported: wait for the engine
  }
  if (!up && o.sharingEligible) c.toggleSharing = !['cancelled', 'error', 'expired', 'unsupported', 'gate'].includes(status);
  return c;
}

/** Aggregate piece states into `groups` contiguous ranges (index-based, never position-based). */
export function pieceGroups(states, groups = GROUP_COUNT) {
  const T = states.length, G = Math.min(groups, T), out = new Array(G);
  for (let g = 0; g < G; g++) {
    const from = Math.floor((g * T) / G), to = Math.floor(((g + 1) * T) / G) - 1;
    let verified = 0, received = 0, retrying = false;
    for (let i = from; i <= to; i++) {
      const s = states[i];
      if (s === P.VERIFIED) verified++;
      else if (s === P.RECEIVED) received++;
      else if (s === P.RETRYING) retrying = true;
    }
    out[g] = { from, to, verified, received, retrying };
  }
  return out;
}

/**
 * @param {object} c engine core state
 * @param {string} c.brand
 * @param {string} c.transferId
 * @param {number} c.sequence
 * @param {'download'|'upload'} c.direction
 * @param {'hybrid'|'https'} c.mode
 * @param {string} c.status
 * @param {{id:string,name:string,type?:string|null,sizeBytes:number|null}} c.file
 * @param {Uint8Array|null} c.states piece states (downloads)
 * @param {(i:number)=>number} [c.lengthOf]
 * @param {Uint8Array|null} [c.sources] 0 = none, 1 = origin, n>1 = peer slot
 */
export function buildSnapshot(c) {
  const up = c.direction === 'upload';
  const T = c.states ? c.states.length : null;
  const snap = {
    protocol: PROTOCOL,
    brand: c.brand,
    transferId: c.transferId,
    sequence: c.sequence,
    direction: c.direction,
    mode: c.mode,
    status: c.status,
    file: { id: c.file.id, name: c.file.name, type: c.file.type ?? null, sizeBytes: c.file.sizeBytes ?? null, totalChunks: up ? null : T },
    verifiedBytes: c.verifiedBytes || 0,
    verifiedChunks: c.verifiedChunks || 0,
    receivedBytes: c.receivedBytes || 0,
    uploadedBytes: up ? c.uploadedBytes || 0 : null,
    metrics: {
      downloadBytesPerSecond: c.metrics?.downloadBytesPerSecond ?? null,
      uploadBytesPerSecond: c.metrics?.uploadBytesPerSecond ?? null,
      etaSeconds: c.metrics?.etaSeconds ?? null,
      activePeerSources: up ? null : c.metrics?.activePeerSources ?? 0,
      usingOrigin: up ? null : c.metrics?.usingOrigin ?? true,
      peerIds: up ? undefined : c.metrics?.peerIds,
    },
    sharing: c.sharing ?? null,
    capabilities: capabilitiesFor(c.status, { direction: c.direction, sharingEligible: !!c.sharingEligible }),
    access: c.access ?? { state: 'allowed', allowPreview: true, allowDownload: true, allowShare: !!c.sharingEligible, allowPublicTorrent: !!c.sharingEligible, gate: 'none', waitSeconds: null },
    storage: c.storage ?? null,
    error: c.error ?? null,
  };
  if (snap.metrics.peerIds === undefined) delete snap.metrics.peerIds;
  if (!up && T) {
    if (T > CHUNK_LIST_LIMIT) snap.pieceGroups = pieceGroups(c.states);
    else {
      const ch = new Array(T);
      for (let i = 0; i < T; i++) {
        const s = c.states[i], src = c.sources ? c.sources[i] : 0;
        ch[i] = { index: i, length: c.lengthOf(i), state: NAMES[s], source: s === P.PENDING || !src ? null : src === 1 ? 'origin' : `peer:${src - 2}` };
      }
      snap.chunks = ch;
    }
  }
  return snap;
}

/** Floored percentage, 100 only with every byte (mirrors the views' rule). */
export function percent(snap) {
  const size = snap.file && snap.file.sizeBytes;
  if (!size) return null;
  const v = Math.min(size, snap.direction === 'upload' ? snap.uploadedBytes || 0 : snap.verifiedBytes || 0);
  return v >= size ? 100 : Math.min(99, Math.floor((v / size) * 100));
}
